/**
 * DOĞRULAMA SONRASI GÜVENLİ DÖNÜŞ (FAZ 0A).
 *
 * ### ⚠ NEDEN VAR
 *
 * Doğrulanmamış e-postalı kullanıcı temsilci davetinde ölü uca düşüyordu: ekran "önce
 * doğrulayın" diyor, doğrulama sayfası ise bitince her hâlde `/tenants`'a atıyordu —
 * kullanıcı davete bir daha dönemiyordu. Dönüş hedefini taşımak gerekiyor ve taşınan her
 * hedef bir açık yönlendirme adayıdır.
 *
 * ### ⚠ İZİN LİSTESİ, KARA LİSTE DEĞİL
 *
 * "Şu kalıpları reddet" yaklaşımı her yeni kaçış biçiminde (protokol-göreli `//`, ters
 * eğik çizgi, kodlanmış ayraç, `javascript:`) bir kez daha yamanmak zorunda kalır ve bir
 * gün biri unutulur. Burada tersi: yalnız BİLİNEN dönüş rotaları kabul ediliyor, gerisi
 * `null`'a düşüyor ve çağıran güvenli varsayılanına gidiyor.
 *
 * Yeni bir dönüş hedefi eklemek bu listeye satır yazmayı gerektirir — bilinçli sürtünme.
 *
 * ### ⚠ SORGU VE FRAGMENT KORUNUYOR
 *
 * Davet token'ı sorgu dizesinde yaşıyor. Yalnız yolu döndürseydik kullanıcı davete
 * değil boş bir davet ekranına dönerdi (talimatın 5. maddesi: "mevcut davet
 * kimliği/tokenı güvenli biçimde korunmalıdır").
 */

/** Dönüş hedefinin taşındığı sorgu parametresi — iki taraf da buradan okuyor. */
export const ISTENEN_DONUS_PARAM = "returnTo";

/**
 * Protokol-göreli biçim mi: `//host` ve `/\host` tarayıcıda MUTLAK davranır.
 *
 * ### ⚠ NEDEN AYRI VE DIŞA AÇIK
 *
 * Bu, izin listesinin ARKASINDAKİ ikinci duvar. Yalnız `guvenliDonusYolu` içinden
 * ölçmeye çalışınca kaldırılması 0 fail veriyordu (ölçüldü) — çünkü izin listesi TAM
 * EŞLEŞME yapıyor ve `//kotu.example` gibi bir değerin yolu asla tek eğik çizgiyle
 * başlayan izinli yollardan birine eşit olamıyor. İki savunma örtüşüyor.
 *
 * Örtüşen savunma "ölçülemez" demek değil: her katman KENDİ SEAMİNDE ölçülür. Duvar
 * bu yüzden kendi fonksiyonu ve kendi testi var. Böylece izin listesi bir gün gevşese
 * bile (biri `startsWith` yazarsa `//kotu.example/davet` geçerdi) bu duvarın tek başına
 * ne yaptığı ölçülmüş oluyor.
 */
export function protokolGoreliMi(deger: string): boolean {
  return deger.startsWith("//") || deger.startsWith("/\\");
}

/**
 * İzinli dönüş yolları.
 *
 * ⚠ TAM EŞLEŞME: `startsWith` kullansaydık `/davetler-sahte` de `/davet` ön ekini
 * taşıdığı için geçerdi. Yol, sorgu/fragment ayrıldıktan SONRA birebir karşılaştırılıyor.
 */
const IZINLI_YOLLAR: readonly string[] = [
  "/davet",
  "/katilimci-davet",
  "/temsilci-davet",
  "/davetlerim",
];

/**
 * Güvenliyse yolu (sorgu ve fragment dahil) döndürür; değilse `null`.
 *
 * ⚠ Mutlak adres HİÇ ÇÖZÜMLENMİYOR: `new URL(...)` ile ayrıştırıp origin karşılaştırmak
 * da bir yoldu ama o yol, tarayıcının çözümleme kurallarını taklit etmeyi gerektirir ve
 * fark bir gün sızdırır. Değer `/` ile başlamıyorsa daha ilk adımda düşüyor.
 */
export function guvenliDonusYolu(deger: string | null | undefined): string | null {
  if (!deger) return null;
  const aday = deger.trim();
  if (aday.length === 0) return null;

  if (!aday.startsWith("/")) return null;
  if (protokolGoreliMi(aday)) return null;

  const yol = aday.split(/[?#]/)[0] ?? "";
  if (!IZINLI_YOLLAR.includes(yol)) return null;

  return aday;
}
